const path = require('path')
const configment = require('@adc/configment')

class InvalidProxyRouteError extends Error {
  constructor(endpoint) {
    super()
    // Try and be useful here, and try *helping* the user by printing out suggested valid routes.
    this.message = `Cannot whitelist ${endpoint}. See core-utils for more details.`
  }
}

/*
  converts a string to a hashed version of that string
  e.g.
    http://10.1.14.187:4202
  becomes
    h2d695
*/
function hash(str) {
  var hash = 0
  if (str.length === 0) return hash
  for (var i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = hash & hash
  }
  return hash.toString(36)
}

/*
  given an api endpoint, determines what the proxy route should look like.
  this is fully deterministic, so both the server and client can run it and
  get the same results. server runs it to mount the proxy in express, client
  runs it to know which proxy to call.

  e.g.
    http://10.1.14.187:4202/pal/cre/v1/users/current_user

    for an app mounted on /commercial/listing/ becomes

    /commercial/listing/cu/api/h2d695/pal/cre/v1/users/current_user
*/
const defaultProxyOptions = {
  // @type Array
  excludeHeaders: ['Cookie'],

  // @type String
  token: 'any',

  // @type Boolean
  enforceCriticalArea: false,
}

function generateProxyRoute(definition) {
  const basePrefix = configment.get('URL_BASE_PREFIX')
  let endpoint = ''
  let base = {}

  // We check for a string or an object here to be backwards compatible and to
  // enable a short form and a long form definition. Most routes will only require using a string
  // but for those that require greater control, an object can be used.
  if (typeof definition === 'string') {
    endpoint = definition
  } else {
    endpoint = definition.endpoint
    base = definition
  }

  // Fail fast for invalid proxy routes
  if (!usesProxy(endpoint)) {
    throw new InvalidProxyRouteError(endpoint)
  }

  let url

  try {
    url = new URL(endpoint)
  } catch (e) {
    throw new InvalidProxyRouteError(endpoint)
  }

  // COMPAT
  // When this was first written, it was using the legacy node url API, which
  // has since been replaced by the WHATWG URL API. Whenever you try and assign back to a URL
  // object now, it will normalize the string casing by downcasing, which changes the hash value, which
  // is why this is assigned to a new variable.
  const maskedHost = url.host.replace(
    /(\d{1,3}\.\d{1,3}\.\d{1,3}\.)\d{1,3}(:\d{2,4})/,
    '$1XXX$2'
  )

  // NOTE: using '/cu/api' below, in cases where basePrefix is '' to ensure the leading slash
  return Object.assign({}, defaultProxyOptions, base, {
    route: path.join(basePrefix, '/cu/api', hash(maskedHost), url.pathname),
    endpoint,
  })
}

/*
  determines when the client should try to call the proxy instead of use fetch directly
  current rule is that we proxy if we access an ip with a 4 digit port,
  a .local:#### domain, or a .ten-x.io:#### domain
*/
function usesProxy(route) {
  return !!(
    route.match(/http:\/\/localhost:\d{2,4}/) ||
    route.match(/http:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}:\d{2,4}/) ||
    route.match(/http:\/\/.*\.local:\d{4}/) ||
    route.match(/http:\/\/.*\.tenx_overlay:\d{4}/) ||
    route.match(/http:\/\/.*\.ten-x\.cloud(:\d{4})?/) ||
    route.match(/http:\/\/.*\.ten-x\.io:\d{4}/) ||
    // By not embedding the cluster environment into the DNS entry, we can be sure that if we keep the
    // same cluster tolopogy in all environments, we can list the location to find the service once for all
    // applications.
    //
    // https://github.com/kubernetes/dns/blob/master/docs/specification.md
    route.match(/http:\/\/(.*)\.(.*)\.svc\.cluster\.local(:\d{2,4})?/) ||
    route.match(/http:\/\/(.*)\.(default|backend|app)(:\d{2,4})?/) ||
    route.match(
      /https:\/\/(service|api)\.(ci|qa|qause2|prod|produse2)\.aucn\.io(:\d{2,4})?/
    )
  )
}

module.exports = {
  generateProxyRoute,
  usesProxy,
}
